// ---------------------------------------------------------------------------
// Full catalog with category filters.
//
// Reuses the existing `.catalog` and `.catalog-card` design; the filter chips
// borrow the `.size-option` treatment so the page reads as part of the same set.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/apiClient.js';
import { useSite } from '../context/SiteProvider.jsx';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { Reveal } from '../components/common/Reveal.jsx';
import { RichText } from '../components/common/RichText.jsx';
import { CatalogCard } from '../components/product/CatalogCard.jsx';
import { CatalogGridSkeleton } from '../components/common/Skeletons.jsx';

export default function Shop() {
  const { t, pick } = useSite();
  const [params, setParams] = useSearchParams();
  const category = params.get('category') ?? 'all';
  usePageMeta(t('navShop'), t('shopIntro'));

  const { data: taxonomy } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories'),
    staleTime: 10 * 60 * 1000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['products', 'summary'],
    queryFn: () => api.get('/products'),
    staleTime: 5 * 60 * 1000,
  });

  const products = data?.products ?? [];
  const categories = taxonomy?.categories ?? [];

  const visible = useMemo(
    () => (category === 'all' ? products : products.filter((product) => product.category?.slug === category)),
    [products, category],
  );

  const setCategory = (slug) => {
    if (slug === 'all') setParams({});
    else setParams({ category: slug });
  };

  return (
    <main id="main">
      <section className="catalog section-pad shop-page">
        <Reveal className="section-head">
          <div>
            <p className="eyebrow">{t('shopEyebrow')}</p>
            <RichText as="h2" html={t('shopTitle')} />
          </div>
          <p>{t('shopIntro')}</p>
        </Reveal>

        <div className="shop-filters" role="group" aria-label={t('filterAll')}>
          <button
            type="button"
            className={`size-option shop-filter${category === 'all' ? ' selected' : ''}`}
            onClick={() => setCategory('all')}
          >
            <span>{t('filterAll')}</span>
            <strong>{products.length}</strong>
          </button>
          {categories.map((entry) => {
            const count = products.filter((product) => product.category?.slug === entry.slug).length;
            return (
              <button
                key={entry.id}
                type="button"
                className={`size-option shop-filter${category === entry.slug ? ' selected' : ''}`}
                onClick={() => setCategory(entry.slug)}
              >
                <span>{pick(entry.name)}</span>
                <strong>{count}</strong>
              </button>
            );
          })}
        </div>

        <div className="catalog-grid">
          {isLoading ? (
            <CatalogGridSkeleton count={8} />
          ) : visible.length ? (
            visible.map((product) => <CatalogCard key={product.id} product={product} />)
          ) : (
            <p className="shop-empty">{t('noProducts')}</p>
          )}
        </div>
      </section>
    </main>
  );
}
